import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/query/react'
import { type ApiResponseSerializer } from '@v2/commons/interface/ApiResponseSerializer'
import {
  type IGetTasksParams,
  type ITask,
  type MutateTaskData,
  type MutateTasksData,
  TaskTypeEnum,
} from '@v2/domains/task/task.types'
import { rootApi } from '@v2/store/api/rootApi'

export const TAGS_TASK = {
  TaskByPersonId: 'TaskByPersonId',
}

export const tasksApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getTasks: builder.query<
      ApiResponseSerializer<ITask>,
      { params: IGetTasksParams; organizationId?: string }
    >({
      query: ({ params }) => ({
        url: 'v4/tasks',
        params,
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName, queryArgs, endpointDefinition }) => {
        const { offset, ...rest } = queryArgs.params
        return defaultSerializeQueryArgs({
          endpointName,
          endpointDefinition,
          queryArgs: { ...rest, organizationId: queryArgs.organizationId },
        })
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.params.offset === 0) {
          return newItems
        }
        return {
          ...newItems,
          records: [...(currentCache?.records || []), ...newItems.records],
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          !!currentArg?.params.offset &&
          currentArg?.params.offset !== previousArg?.params.offset
        )
      },
    }),
    assignTasks: builder.mutation<{ OK: boolean }, MutateTasksData>({
      query: (queryArgs) => ({
        url: 'v5/tasks/multiple',
        method: 'POST',
        data: queryArgs.body,
      }),
    }),
    assignTask: builder.mutation<{ OK: boolean }, MutateTaskData>({
      invalidatesTags: [TAGS_TASK.TaskByPersonId],
      query: (queryBody) => ({
        url: 'v4/tasks',
        method: 'POST',
        data: queryBody,
      }),
    }),
    updateTask: builder.mutation<{ OK: boolean }, MutateTaskData>({
      invalidatesTags: [TAGS_TASK.TaskByPersonId],
      query: (queryBody) => ({
        url: 'v4/tasks',
        method: 'PATCH',
        data: queryBody,
      }),
    }),
    getTaskByPersonId: builder.query<
      ApiResponseSerializer<ITask>,
      {
        personId: string
        search: string
        types: TaskTypeEnum[]
      }
    >({
      providesTags: (result, err, args) => {
        const { search } = args as { search: string }
        return [TAGS_TASK.TaskByPersonId, search]
      },
      query: ({ personId, search, types }) => {
        return {
          url: `/tasks/${personId}`,
          method: 'GET',
          params: {
            limit: 50,
            search,
            types,
          },
        }
      },
    }),
  }),
})

export const {
  useGetTasksQuery,
  useGetTaskByPersonIdQuery,
  useAssignTasksMutation,
  useAssignTaskMutation,
  useUpdateTaskMutation,
} = tasksApi
